// Auditor engagements (PLAN-C4.5 D12) — the AUDITED tenant's view of its external-auditor
// engagements (kind + status), with a drill-through to the evidence register. Read-only: creating
// an engagement requires an auditor-org directory (FK auditor_org_id) that is counsel-gated content
// not yet seeded — flagged below, same pattern as ticket-kinds. The external auditor's own read
// portal (auditor-session auth, separate surface) is tracked separately. Source: B7-AUDITOR-SURFACE.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { humanizeEnum } from "../lib/enum-labels";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";

interface AuditorRow extends Record<string, unknown> {
  id?: string;
  engagementKind?: unknown;
  engagementStatus?: unknown;
  auditorOrgId?: unknown;
}

export function AuditorRoute() {
  const { t } = useI18n();
  const { data, isLoading, error } = useQuery({ queryKey: ["auditor-engagements"], queryFn: () => api.listAuditorEngagements() });
  const rows = (data ?? []) as AuditorRow[];

  const columns: Column<AuditorRow>[] = [
    { key: "kind", header: "Kind", render: (r) => humanizeEnum(r.engagementKind) },
    {
      key: "status", header: "Status",
      render: (r) => {
        const status = String(r.engagementStatus ?? "");
        return <Badge variant={status === "active" ? "default" : "secondary"}>{String(r.engagementStatus ?? "—")}</Badge>;
      },
    },
    { key: "org", header: "Auditor org", render: (r) => <span className="font-mono text-xs text-muted-foreground">{String(r.auditorOrgId ?? "—")}</span> },
    { key: "evidence", header: "Evidence", render: () => <Link to="/bewijscontainers" className="text-primary hover:underline">Open evidence →</Link> },
  ];

  return (
    <section>
      <PageHeader
        title={t("page.auditors")}
        description={t("page.auditors.sub")}
      />
      <p className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        Creating an engagement needs the auditor-org directory (pending content seeding). Existing engagements are shown below; evidence is in the <Link to="/bewijscontainers" className="text-primary hover:underline">evidence register</Link>.
      </p>

      {error && <p className="mb-4 text-sm text-destructive">Failed to load: {(error as Error).message}</p>}

      <DataTable<AuditorRow>
        columns={columns}
        rows={rows}
        loading={isLoading}
        getRowId={(r) => String(r.id ?? rows.indexOf(r))}
        empty="No auditor engagements yet."
      />
    </section>
  );
}
